import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Pencil,
  Trash2,
  LogOut,
  Loader2,
  X,
  ExternalLink,
  Briefcase,
  History,
  FileText,
  EyeOff,
  MessageCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { toast, Toaster } from "sonner";

const TABS = [
  { id: "projects", label: "Selected works", icon: Briefcase },
  { id: "timeline", label: "Trajectory", icon: History },
  { id: "posts", label: "Blog", icon: FileText },
  { id: "comments", label: "Comments", icon: MessageCircle },
];

const EMPTY_PROJECT = {
  id: null,
  title: "",
  year: "",
  tag: "",
  summary: "",
  stack: "",
  image: "",
  order: 0,
};

const EMPTY_TIMELINE = {
  id: null,
  period: "",
  title: "",
  org: "",
  detail: "",
  order: 0,
};

const EMPTY_POST = {
  id: null,
  slug: "",
  title: "",
  excerpt: "",
  category: "",
  read_time: "",
  date: new Date().toISOString().slice(0, 10),
  cover: "",
  body: "",
  published: true,
  order: 0,
};

function slugify(value = "") {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);

  useEffect(() => {
    document.title = "Dashboard — A.R.U.";
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t, b, c] = await Promise.all([
        api.get("/projects"),
        api.get("/timeline"),
        api.get("/posts/admin"),
        supabase
          .from("comments")
          .select("*")
          .order("created_at", { ascending: false }),
      ]);
      setProjects(p.data);
      setTimeline(t.data);
      setPosts(b.data);
      setComments(c.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const openCreate = (kind) => {
    if (kind === "project") {
      setEditor({
        kind: "project",
        data: { ...EMPTY_PROJECT, order: projects.length + 1 },
      });
    } else if (kind === "timeline") {
      setEditor({
        kind: "timeline",
        data: { ...EMPTY_TIMELINE, order: timeline.length + 1 },
      });
    } else {
      setEditor({
        kind: "post",
        data: { ...EMPTY_POST, order: posts.length + 1 },
      });
    }
  };

  const openEdit = (kind, item) => {
    if (kind === "project") {
      setEditor({
        kind: "project",
        data: { ...item, stack: (item.stack || []).join(", ") },
      });
    } else if (kind === "timeline") {
      setEditor({ kind: "timeline", data: { ...item } });
    } else {
      setEditor({
        kind: "post",
        data: { ...item, body: (item.body || []).join("\n\n") },
      });
    }
  };

  const remove = async (kind, id) => {
    const label =
      kind === "project" ? "project" : kind === "timeline" ? "timeline entry" : "post";
    if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
    try {
      const path =
        kind === "project"
          ? "/projects"
          : kind === "timeline"
          ? "/timeline"
          : "/posts";
      await api.delete(`${path}/${id}`);
      toast.success("Deleted.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeComment = async (id) => {
    if (!window.confirm("Delete this comment? This cannot be undone.")) return;
    const prev = comments;
    setComments((c) => c.filter((x) => x.id !== id && x.parent_id !== id));
    const { error } = await supabase.from("comments").delete().eq("id", id);
    if (error) {
      toast.error(error.message);
      setComments(prev);
    } else {
      toast.success("Comment deleted.");
    }
  };

  const save = async (kind, data) => {
    try {
      if (kind === "project") {
        const payload = {
          title: data.title,
          year: data.year,
          tag: data.tag,
          summary: data.summary,
          image: data.image,
          order: Number(data.order) || 0,
          stack:
            typeof data.stack === "string"
              ? data.stack
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : data.stack,
        };
        if (data.id) await api.put(`/projects/${data.id}`, payload);
        else await api.post("/projects", payload);
      } else if (kind === "timeline") {
        const payload = {
          period: data.period,
          title: data.title,
          org: data.org,
          detail: data.detail,
          order: Number(data.order) || 0,
        };
        if (data.id) await api.put(`/timeline/${data.id}`, payload);
        else await api.post("/timeline", payload);
      } else {
        const payload = {
          slug: data.slug || slugify(data.title),
          title: data.title,
          excerpt: data.excerpt,
          category: data.category,
          read_time: data.read_time,
          date: data.date,
          cover: data.cover,
          published: !!data.published,
          order: Number(data.order) || 0,
          body:
            typeof data.body === "string"
              ? data.body
                  .split(/\n\s*\n/)
                  .map((s) => s.trim())
                  .filter(Boolean)
              : data.body,
        };
        if (data.id) await api.put(`/posts/${data.id}`, payload);
        else await api.post("/posts", payload);
      }
      toast.success("Saved.");
      setEditor(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <main
      data-testid="admin-dashboard-root"
      className="min-h-screen bg-black text-white"
    >
      <Toaster theme="dark" position="top-center" />

      <header className="sticky top-0 z-40 border-b border-white/5 bg-black/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="dashboard-logo"
              className="font-heading text-lg font-medium tracking-tight"
            >
              A.R.U.
            </Link>
            <span className="text-zinc-700">/</span>
            <span className="text-zinc-300 text-sm">Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="dashboard-view-site"
              className="hidden md:inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white border border-white/10 hover:border-white/30 px-3.5 py-2 rounded-full transition-colors"
            >
              View site
              <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75} />
            </Link>
            <button
              data-testid="dashboard-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-sm text-white bg-white/5 hover:bg-white/10 border border-white/10 px-3.5 py-2 rounded-full transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.75} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-6">
        <div className="text-sm text-zinc-500">Signed in as {user?.email}</div>
        <h1 className="font-heading mt-2 text-4xl md:text-5xl font-light tracking-tight">
          Curate the public portfolio.
        </h1>
        <p className="mt-2 text-zinc-400 font-light text-base max-w-2xl">
          Anything you add, edit, or remove here appears instantly on the public
          site.
        </p>

        <div className="mt-8 inline-flex flex-wrap items-center gap-1 bg-white/[0.03] border border-white/10 rounded-full p-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`dashboard-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full transition-colors ${
                tab === id
                  ? "bg-white text-black"
                  : "text-zinc-300 hover:text-white"
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="flex items-center justify-between mb-5">
          <div className="text-sm text-zinc-500">
            {tab === "projects" &&
              `${projects.length} project${projects.length === 1 ? "" : "s"}`}
            {tab === "timeline" &&
              `${timeline.length} timeline ${timeline.length === 1 ? "entry" : "entries"}`}
            {tab === "posts" &&
              `${posts.length} post${posts.length === 1 ? "" : "s"}`}
            {tab === "comments" &&
              `${comments.length} comment${comments.length === 1 ? "" : "s"}`}
          </div>
          {tab !== "comments" && (
            <button
              data-testid={
                tab === "projects"
                  ? "add-project-btn"
                  : tab === "timeline"
                  ? "add-timeline-btn"
                  : "add-post-btn"
              }
              onClick={() =>
                openCreate(
                  tab === "projects" ? "project" : tab === "timeline" ? "timeline" : "post"
                )
              }
              className="inline-flex items-center gap-2 bg-white text-black px-4 py-2 rounded-full text-sm font-medium hover:scale-[1.04] transition-transform"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2} />
              {tab === "projects"
                ? "New project"
                : tab === "timeline"
                ? "New entry"
                : "New post"}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : tab === "projects" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {projects.map((p) => (
              <ProjectRow
                key={p.id}
                project={p}
                onEdit={() => openEdit("project", p)}
                onDelete={() => remove("project", p.id)}
              />
            ))}
            {projects.length === 0 && <EmptyState label="No projects yet." />}
          </div>
        ) : tab === "timeline" ? (
          <div className="space-y-4">
            {timeline.map((t) => (
              <TimelineRow
                key={t.id}
                item={t}
                onEdit={() => openEdit("timeline", t)}
                onDelete={() => remove("timeline", t.id)}
              />
            ))}
            {timeline.length === 0 && (
              <EmptyState label="No timeline entries yet." />
            )}
          </div>
        ) : tab === "posts" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {posts.map((p) => (
              <PostRow
                key={p.id}
                post={p}
                onEdit={() => openEdit("post", p)}
                onDelete={() => remove("post", p.id)}
              />
            ))}
            {posts.length === 0 && <EmptyState label="No posts yet." />}
          </div>
        ) : (
          <div className="space-y-3">
            {comments.map((c) => (
              <CommentRow
                key={c.id}
                comment={c}
                onDelete={() => removeComment(c.id)}
              />
            ))}
            {comments.length === 0 && <EmptyState label="No comments yet." />}
          </div>
        )}
      </section>

      <AnimatePresence>
        {editor && (
          <EditorModal
            kind={editor.kind}
            data={editor.data}
            onClose={() => setEditor(null)}
            onSave={(d) => save(editor.kind, d)}
          />
        )}
      </AnimatePresence>
    </main>
  );
}

function EmptyState({ label }) {
  return (
    <div className="col-span-full text-center py-20 border border-dashed border-white/10 rounded-2xl text-zinc-500 text-sm">
      {label}
    </div>
  );
}

function CommentRow({ comment: c, onDelete }) {
  return (
    <div
      data-testid={`admin-comment-row-${c.id}`}
      className="group flex items-start gap-4 bg-white/[0.02] border border-white/10 rounded-2xl p-4 hover:border-white/20 transition-colors"
    >
      {c.author_avatar ? (
        <img
          src={c.author_avatar}
          alt={c.author_name}
          className="w-9 h-9 rounded-full border border-white/10 shrink-0"
        />
      ) : (
        <div className="w-9 h-9 rounded-full bg-white/10 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm flex-wrap">
          <span className="text-white font-medium">@{c.author_name}</span>
          <span className="text-zinc-600">·</span>
          <Link
            to={`/blog/${c.post_slug}#comments`}
            className="text-zinc-400 hover:text-white truncate"
          >
            {c.post_slug}
          </Link>
          {c.parent_id && (
            <span className="text-xs text-zinc-500 border border-white/10 rounded-full px-2 py-0.5">
              reply
            </span>
          )}
          {c.edited_at && <span className="text-xs text-zinc-600">(edited)</span>}
        </div>
        <p className="mt-1.5 text-sm text-zinc-300 font-light whitespace-pre-wrap break-words">
          {c.body}
        </p>
        <div className="mt-1.5 text-xs text-zinc-600">
          {new Date(c.created_at).toLocaleString()}
        </div>
      </div>
      <button
        data-testid={`admin-comment-delete-${c.id}`}
        onClick={onDelete}
        className="shrink-0 text-zinc-500 hover:text-red-400 transition-colors p-2"
        aria-label="Delete comment"
      >
        <Trash2 className="w-4 h-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}

function ProjectRow({ project, onEdit, onDelete }) {
  return (
    <div
      data-testid={`project-row-${project.id}`}
      className="group relative bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden flex flex-col hover:border-white/20 transition-colors"
    >
      <div className="aspect-[16/9] bg-black overflow-hidden">
        {project.image && (
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover opacity-80"
          />
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="text-zinc-500">
            #{project.order} · {project.year || "—"}
          </span>
          <span className="text-zinc-400 bg-white/[0.03] border border-white/10 px-2.5 py-1 rounded-full">
            {project.tag || "Untagged"}
          </span>
        </div>
        <h3 className="font-heading mt-3 text-lg font-medium tracking-tight">
          {project.title || "Untitled"}
        </h3>
        <p className="mt-2 text-sm text-zinc-400 font-light line-clamp-3">
          {project.summary}
        </p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(project.stack || []).map((s) => (
            <span
              key={s}
              className="text-xs text-zinc-400 bg-white/[0.03] border border-white/10 px-2 py-0.5 rounded-full"
            >
              {s}
            </span>
          ))}
        </div>
        <ActionBar
          editTestId={`edit-project-${project.id}`}
          deleteTestId={`delete-project-${project.id}`}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}

function TimelineRow({ item, onEdit, onDelete }) {
  return (
    <div
      data-testid={`timeline-row-${item.id}`}
      className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-4 hover:border-white/20 transition-colors"
    >
      <div className="md:w-56 shrink-0">
        <div className="text-sm text-zinc-500">#{item.order}</div>
        <div className="mt-1 text-sm text-zinc-300">{item.period}</div>
      </div>
      <div className="flex-1">
        <h3 className="font-heading text-lg font-medium tracking-tight">
          {item.title}
        </h3>
        <div className="text-sm text-zinc-400 mt-0.5">{item.org}</div>
        <p className="mt-2 text-sm text-zinc-400 font-light line-clamp-2">
          {item.detail}
        </p>
      </div>
      <div className="flex items-center gap-2 md:flex-col md:items-stretch">
        <button
          data-testid={`edit-timeline-${item.id}`}
          onClick={onEdit}
          className="inline-flex items-center justify-center gap-1.5 text-sm text-zinc-200 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 rounded-full transition-colors"
        >
          <Pencil className="w-3 h-3" strokeWidth={1.75} />
          Edit
        </button>
        <button
          data-testid={`delete-timeline-${item.id}`}
          onClick={onDelete}
          className="inline-flex items-center justify-center gap-1.5 text-sm text-red-300 hover:text-red-200 border border-red-500/20 hover:border-red-500/40 px-3 py-1.5 rounded-full transition-colors"
        >
          <Trash2 className="w-3 h-3" strokeWidth={1.75} />
          Delete
        </button>
      </div>
    </div>
  );
}

function PostRow({ post, onEdit, onDelete }) {
  return (
    <div
      data-testid={`post-row-${post.id}`}
      className="group relative bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden flex flex-col hover:border-white/20 transition-colors"
    >
      <div className="aspect-[16/9] bg-black overflow-hidden relative">
        {post.cover && (
          <img
            src={post.cover}
            alt={post.title}
            className="w-full h-full object-cover opacity-80"
          />
        )}
        {!post.published && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/70 border border-white/15 px-2.5 py-1 rounded-full text-xs text-zinc-200">
            <EyeOff className="w-3 h-3" strokeWidth={1.75} />
            Draft
          </div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="text-zinc-500">
            #{post.order} · {post.category || "Uncategorised"}
          </span>
          <span className="text-zinc-400">{post.date}</span>
        </div>
        <h3 className="font-heading mt-3 text-lg font-medium tracking-tight">
          {post.title || "Untitled post"}
        </h3>
        <div className="mt-1 text-xs text-zinc-500">/{post.slug}</div>
        <p className="mt-2 text-sm text-zinc-400 font-light line-clamp-3">
          {post.excerpt}
        </p>
        <ActionBar
          editTestId={`edit-post-${post.id}`}
          deleteTestId={`delete-post-${post.id}`}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}

function ActionBar({ editTestId, deleteTestId, onEdit, onDelete }) {
  return (
    <div className="mt-5 flex items-center gap-2 pt-4 border-t border-white/5">
      <button
        data-testid={editTestId}
        onClick={onEdit}
        className="inline-flex items-center gap-1.5 text-sm text-zinc-200 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 rounded-full transition-colors"
      >
        <Pencil className="w-3 h-3" strokeWidth={1.75} />
        Edit
      </button>
      <button
        data-testid={deleteTestId}
        onClick={onDelete}
        className="inline-flex items-center gap-1.5 text-sm text-red-300 hover:text-red-200 border border-red-500/20 hover:border-red-500/40 px-3 py-1.5 rounded-full transition-colors"
      >
        <Trash2 className="w-3 h-3" strokeWidth={1.75} />
        Delete
      </button>
    </div>
  );
}

function EditorModal({ kind, data: initial, onClose, onSave }) {
  const [data, setData] = useState(initial);
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setData((d) => ({ ...d, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  const kindLabel =
    kind === "project" ? "Project" : kind === "timeline" ? "Timeline entry" : "Blog post";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="editor-modal"
    >
      <motion.form
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.98 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSave}
        className="w-full max-w-2xl bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 md:p-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-sm text-zinc-500">
              {data.id ? "Edit" : "Create"}
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-light tracking-tight mt-1">
              {kindLabel}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="editor-close"
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 inline-flex items-center justify-center"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {kind === "project" && <ProjectFields data={data} set={set} />}
          {kind === "timeline" && <TimelineFields data={data} set={set} />}
          {kind === "post" && <PostFields data={data} set={set} />}
        </div>

        <div className="flex items-center justify-end gap-3 mt-8 pt-5 border-t border-white/5">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-zinc-300 hover:text-white px-4 py-2 rounded-full transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="editor-save"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-medium hover:scale-[1.04] disabled:opacity-50 disabled:hover:scale-100 transition-transform"
          >
            {saving ? "Saving…" : data.id ? "Save changes" : "Create"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}

function ProjectFields({ data, set }) {
  return (
    <>
      <Field label="Title" full>
        <input
          data-testid="editor-input-title"
          required
          value={data.title}
          onChange={(e) => set("title", e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Year">
        <input
          data-testid="editor-input-year"
          value={data.year}
          onChange={(e) => set("year", e.target.value)}
          className={inputCls}
          placeholder="2025"
        />
      </Field>
      <Field label="Tag">
        <input
          data-testid="editor-input-tag"
          value={data.tag}
          onChange={(e) => set("tag", e.target.value)}
          className={inputCls}
          placeholder="TEKNOFEST · Team Leader"
        />
      </Field>
      <Field label="Image URL" full>
        <input
          data-testid="editor-input-image"
          value={data.image}
          onChange={(e) => set("image", e.target.value)}
          className={inputCls}
          placeholder="https://…"
        />
      </Field>
      <Field label="Summary (separate paragraphs with a blank line)" full>
        <textarea
          data-testid="editor-input-summary"
          rows={5}
          value={data.summary}
          onChange={(e) => set("summary", e.target.value)}
          className={`${inputCls} resize-none`}
        />
      </Field>
      <Field label="Stack (comma-separated)" full>
        <input
          data-testid="editor-input-stack"
          value={data.stack}
          onChange={(e) => set("stack", e.target.value)}
          className={inputCls}
          placeholder="ROS, Python, ESP32"
        />
      </Field>
      <Field label="Order">
        <input
          data-testid="editor-input-order"
          type="number"
          value={data.order}
          onChange={(e) => set("order", e.target.value)}
          className={inputCls}
        />
      </Field>
    </>
  );
}

function TimelineFields({ data, set }) {
  return (
    <>
      <Field label="Period" full>
        <input
          data-testid="editor-input-period"
          required
          value={data.period}
          onChange={(e) => set("period", e.target.value)}
          className={inputCls}
          placeholder="Feb 2024 to Sep 2024"
        />
      </Field>
      <Field label="Title" full>
        <input
          data-testid="editor-input-title"
          required
          value={data.title}
          onChange={(e) => set("title", e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Organisation" full>
        <input
          data-testid="editor-input-org"
          value={data.org}
          onChange={(e) => set("org", e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Detail" full>
        <textarea
          data-testid="editor-input-detail"
          rows={4}
          value={data.detail}
          onChange={(e) => set("detail", e.target.value)}
          className={`${inputCls} resize-none`}
        />
      </Field>
      <Field label="Order">
        <input
          data-testid="editor-input-order"
          type="number"
          value={data.order}
          onChange={(e) => set("order", e.target.value)}
          className={inputCls}
        />
      </Field>
    </>
  );
}

function PostFields({ data, set }) {
  return (
    <>
      <Field label="Title" full>
        <input
          data-testid="editor-input-title"
          required
          value={data.title}
          onChange={(e) => {
            const v = e.target.value;
            set("title", v);
            // auto-fill slug only when empty or matches previous slugify
            if (!data.id && (!data.slug || data.slug === slugify(data.title))) {
              set("slug", slugify(v));
            }
          }}
          className={inputCls}
        />
      </Field>
      <Field label="Slug (URL)">
        <input
          data-testid="editor-input-slug"
          value={data.slug}
          onChange={(e) => set("slug", slugify(e.target.value))}
          className={inputCls}
          placeholder="my-post-title"
        />
      </Field>
      <Field label="Category">
        <input
          data-testid="editor-input-category"
          value={data.category}
          onChange={(e) => set("category", e.target.value)}
          className={inputCls}
          placeholder="Robotics"
        />
      </Field>
      <Field label="Date (YYYY-MM-DD)">
        <input
          data-testid="editor-input-date"
          type="date"
          value={data.date}
          onChange={(e) => set("date", e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Read time">
        <input
          data-testid="editor-input-read-time"
          value={data.read_time}
          onChange={(e) => set("read_time", e.target.value)}
          className={inputCls}
          placeholder="6 min read"
        />
      </Field>
      <Field label="Cover image URL" full>
        <input
          data-testid="editor-input-cover"
          value={data.cover}
          onChange={(e) => set("cover", e.target.value)}
          className={inputCls}
          placeholder="https://…"
        />
      </Field>
      <Field label="Excerpt (short summary)" full>
        <textarea
          data-testid="editor-input-excerpt"
          rows={2}
          value={data.excerpt}
          onChange={(e) => set("excerpt", e.target.value)}
          className={`${inputCls} resize-none`}
        />
      </Field>
      <Field label="Body. Separate paragraphs with a blank line. Add an image on its own line: ![caption](image-url)" full>
        <textarea
          data-testid="editor-input-body"
          rows={10}
          value={data.body}
          onChange={(e) => set("body", e.target.value)}
          className={`${inputCls} resize-none font-light leading-relaxed`}
        />
        <button
          type="button"
          data-testid="editor-insert-image"
          onClick={() => {
            const cur = (data.body || "").replace(/\s*$/, "");
            const snippet = "![caption](https://image-url)";
            set("body", cur ? `${cur}\n\n${snippet}\n` : `${snippet}\n`);
          }}
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 rounded-full transition-colors"
        >
          <Plus className="w-3 h-3" strokeWidth={2} />
          Insert image
        </button>
        <p className="mt-2 text-xs text-zinc-600">
          Tip: edit the <code className="text-zinc-400">caption</code> and{" "}
          <code className="text-zinc-400">image-url</code> in the inserted line.
          Each image must sit on its own line with a blank line above and below.
        </p>
      </Field>
      <Field label="Order">
        <input
          data-testid="editor-input-order"
          type="number"
          value={data.order}
          onChange={(e) => set("order", e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Status">
        <label className="inline-flex items-center gap-3 bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 cursor-pointer">
          <input
            data-testid="editor-input-published"
            type="checkbox"
            checked={!!data.published}
            onChange={(e) => set("published", e.target.checked)}
            className="accent-white"
          />
          <span className="text-sm text-zinc-200">
            {data.published ? "Published" : "Draft"}
          </span>
        </label>
      </Field>
    </>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-sm text-zinc-400 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-white/30 transition-colors";
